import { useEffect, useMemo, useRef, useState } from "react";
import { IFood } from "../../models/IFood";
import { getAllFood } from "../../services/food";
import {
  DINEIN_MAX_LINES_PER_REQUEST, DINEIN_MAX_QUANTITY, IDineInBill, IDineInNewLine,
} from "../models/IDineIn";
import { addDineInOrderLines, dineInErrorCode, dineInErrorDescription } from "../services/dineInService";

interface DraftLine {
  key: string;
  food: IFood;
  quantity: number;
  note: string;
  upgrades: Record<string, number>;
}

const ADD_ERROR: Record<string, string> = {
  SessionAlreadyPaid: "Bu hesabın ödemesi alınmış, satır eklenemez.",
  SessionNotOpen: "Masa boşaltılmış. Yeniden açmanız gerekiyor.",
  SessionNotFound: "Bu sipariş kodu bulunamadı.",
  MixedCurrency: "Bir hesapta tek para birimi olabilir. Farklı para birimli ürün eklenemez.",
  FoodNotAvailable: "Seçilen ürünlerden biri satışta değil. Menüyü yenileyin.",
  FoodNotInRestaurant: "Seçilen ürünlerden biri bu restorana ait değil. Menüyü yenileyin.",
  FoodNotFound: "Seçilen ürünlerden biri bulunamadı. Menüyü yenileyin.",
  MenuUpgradeNotFound: "Seçilen menü eklerinden biri bulunamadı.",
  MenuUpgradeNotAvailable: "Seçilen menü eklerinden biri satışta değil.",
  MenuUpgradeNotForFood: "Menü eki seçilen ürüne ait değil.",
  RestourantAccessDenied: "Bu restoranda işlem yapma yetkiniz yok.",
};

function money(n: number, symbol?: string): string {
  return `${n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${symbol ? ` ${symbol}` : ""}`;
}

const priceOf = (f: IFood) => Number(f.foodPrice) || 0;
const effectivePriceOf = (f: IFood) => (f.hasDiscount && f.discountedPrice != null ? f.discountedPrice : priceOf(f));

interface Props {
  restaurantId: string;
  orderCode: string;
  /** Hesapta satır varsa para birimi kilitlidir; farklı birimli ürün eklenemez. */
  lockedCurrencyId?: string;
  currencySymbol?: string;
  onClose: () => void;
  onAdded: (bill: IDineInBill) => void;
}

export default function AddOrderLinesModal({
  restaurantId, orderCode, lockedCurrencyId, currencySymbol, onClose, onAdded,
}: Props) {
  const [menu, setMenu] = useState<IFood[] | null>(null);
  const [menuError, setMenuError] = useState("");
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState<DraftLine[]>([]);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [recentKey, setRecentKey] = useState<string | null>(null);
  // Aynı ürün tekrar eklenince de vurgu animasyonu yeniden oynasın diye.
  const [flashTick, setFlashTick] = useState(0);
  const cartRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getAllFood(restaurantId)
      .then((res) => setMenu(res.data ?? []))
      .catch(() => setMenuError("Menü yüklenemedi."));
  }, [restaurantId]);

  // Satışta olmayan ürünleri istemci süzer (sunucu ayrıca reddediyor).
  const sellable = useMemo(() => (menu ?? []).filter((f) => f.foodIsEnabled), [menu]);

  const visible = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    if (!q) return sellable;
    return sellable.filter((f) =>
      f.foodNameTr?.toLocaleLowerCase("tr").includes(q) || f.foodNameEn?.toLocaleLowerCase("tr").includes(q)
    );
  }, [sellable, search]);

  // Son eklenen satır listenin en başına gelir ve açık sarıyla vurgulanır; telefonda
  // menü ile sepet alt alta olduğundan sepet de başa kaydırılır ki eklenen hemen görünsün.
  const addToDraft = (food: IFood) => {
    setError("");
    const existing = draft.find((d) => d.food.id === food.id && !d.note && Object.keys(d.upgrades).length === 0);
    if (existing) {
      const bumped = { ...existing, quantity: Math.min(DINEIN_MAX_QUANTITY, existing.quantity + 1) };
      setDraft((prev) => [bumped, ...prev.filter((d) => d.key !== existing.key)]);
      setRecentKey(existing.key);
    } else {
      const key = `${food.id}-${Date.now()}`;
      setDraft((prev) => [{ key, food, quantity: 1, note: "", upgrades: {} }, ...prev]);
      setRecentKey(key);
    }
    setFlashTick((t) => t + 1);
    cartRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const patchDraft = (key: string, patch: Partial<DraftLine>) =>
    setDraft((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));

  const removeDraft = (key: string) => setDraft((prev) => prev.filter((d) => d.key !== key));

  const setUpgradeQty = (key: string, upgradeId: string, qty: number) =>
    setDraft((prev) => prev.map((d) => {
      if (d.key !== key) return d;
      const next = { ...d.upgrades };
      if (qty <= 0) delete next[upgradeId]; else next[upgradeId] = qty;
      return { ...d, upgrades: next };
    }));

  const draftTotal = draft.reduce((sum, d) => {
    const upg = Object.entries(d.upgrades).reduce((s, [id, q]) => {
      const u = d.food.menuUpgrade?.find((x) => x.id === id);
      return s + (u ? u.menuUpgradePrice * q : 0);
    }, 0);
    return sum + effectivePriceOf(d.food) * d.quantity + upg;
  }, 0);

  const overLimit = draft.length > DINEIN_MAX_LINES_PER_REQUEST;

  const handleSubmit = () => {
    if (draft.length === 0) return;
    if (overLimit) {
      setError(`Tek seferde en fazla ${DINEIN_MAX_LINES_PER_REQUEST} satır gönderilebilir. Siparişi iki tura bölün.`);
      return;
    }
    const lines: IDineInNewLine[] = draft.map((d) => {
      const upgrades = Object.entries(d.upgrades).map(([menuUpgradeId, quantity]) => ({ menuUpgradeId, quantity }));
      return {
        foodId: d.food.id,
        quantity: d.quantity,
        foodOrderNote: d.note.trim() || undefined,
        upgrades: upgrades.length ? upgrades : undefined,
      };
    });

    setSaving(true);
    setError("");
    addDineInOrderLines(orderCode, lines)
      .then((res) => { onAdded(res.data); onClose(); })
      .catch((err) => {
        const code = dineInErrorCode(err);
        setError(ADD_ERROR[code ?? ""] ?? dineInErrorDescription(err) ?? "Ürünler eklenemedi.");
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="dinein-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="dinein-modal dinein-modal--wide">
        <div className="dinein-modal__head">
          <div>
            <div className="dinein-modal__title"><i className="bi bi-basket-fill me-2"></i>Ürün Ekle</div>
            <div className="dinein-modal__sub">Seçtiklerin tek turda hesaba eklenir</div>
          </div>
          <button className="dinein-icon-btn" onClick={onClose} disabled={saving}><i className="bi bi-x-lg"></i></button>
        </div>

        <div className="dinein-picker">
          {/* Menü */}
          <div className="dinein-picker__menu">
            <div className="dinein-search">
              <i className="bi bi-search"></i>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ürün ara..." />
              {search && <button onClick={() => setSearch("")}><i className="bi bi-x-lg"></i></button>}
            </div>

            {menuError ? (
              <div className="dinein-alert dinein-alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{menuError}</span></div>
            ) : !menu ? (
              <div className="dinein-center"><span className="spinner-border spinner-border-sm" /></div>
            ) : visible.length === 0 ? (
              <div className="dinein-center">{search ? `"${search}" ile eşleşen ürün yok.` : "Satışta ürün yok."}</div>
            ) : (
              <div className="dinein-food-list">
                {visible.map((f) => {
                  const blocked = !!lockedCurrencyId && f.currencyId !== lockedCurrencyId;
                  const disc = f.hasDiscount && f.discountedPrice != null;
                  return (
                    <button key={f.id} type="button" className="dinein-food" disabled={blocked}
                      title={blocked ? "Bu ürün hesabın para biriminden farklı" : undefined}
                      onClick={() => addToDraft(f)}>
                      <span className="dinein-food__name">{f.foodNameTr}</span>
                      <span className="dinein-food__price">
                        {disc && <s>{money(priceOf(f))}</s>}
                        <strong>{money(effectivePriceOf(f))}</strong>
                      </span>
                      {blocked && <span className="dinein-badge dinein-badge--warn">farklı para birimi</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sepet */}
          <div className="dinein-picker__cart" ref={cartRef}>
            <div className="dinein-picker__cart-head">
              <span>Eklenecekler</span>
              <span className={overLimit ? "dinein-count dinein-count--over" : "dinein-count"}>
                {draft.length} / {DINEIN_MAX_LINES_PER_REQUEST} satır
              </span>
            </div>

            {draft.length === 0 ? (
              <div className="dinein-center dinein-center--tall">
                <i className="bi bi-arrow-left"></i>
                <div>Soldaki menüden ürün seçin.</div>
              </div>
            ) : (
              <div className="dinein-draft-list">
                {draft.map((d) => {
                  const upgrades = d.food.menuUpgrade?.filter((u) => u.menuUpgradeIsEnabled) ?? [];
                  const expanded = expandedKey === d.key;
                  const recent = d.key === recentKey;
                  return (
                    <div key={recent ? `${d.key}-${flashTick}` : d.key}
                      className={`dinein-draft${recent ? " dinein-draft--recent" : ""}`}>
                      <div className="dinein-draft__top">
                        <span className="dinein-draft__name">{d.food.foodNameTr}</span>
                        <button className="dinein-icon-btn dinein-icon-btn--danger" onClick={() => removeDraft(d.key)}>
                          <i className="bi bi-x-lg"></i>
                        </button>
                      </div>

                      <div className="dinein-draft__row">
                        <div className="dinein-stepper">
                          <button type="button" disabled={d.quantity <= 1}
                            onClick={() => patchDraft(d.key, { quantity: Math.max(1, d.quantity - 1) })}>
                            <i className="bi bi-dash-lg"></i>
                          </button>
                          <input type="number" min={1} max={DINEIN_MAX_QUANTITY} value={d.quantity}
                            onChange={(e) => patchDraft(d.key, { quantity: Math.min(DINEIN_MAX_QUANTITY, Math.max(1, Number(e.target.value) || 1)) })} />
                          <button type="button" disabled={d.quantity >= DINEIN_MAX_QUANTITY}
                            onClick={() => patchDraft(d.key, { quantity: Math.min(DINEIN_MAX_QUANTITY, d.quantity + 1) })}>
                            <i className="bi bi-plus-lg"></i>
                          </button>
                        </div>
                        <span className="dinein-draft__price">
                          {money(effectivePriceOf(d.food) * d.quantity, currencySymbol)}
                        </span>
                      </div>

                      <input className="dinein-note-input" value={d.note} maxLength={500} placeholder="Not (örn. soğansız)"
                        onChange={(e) => patchDraft(d.key, { note: e.target.value })} />

                      {upgrades.length > 0 && (
                        <>
                          <button type="button" className="dinein-link" onClick={() => setExpandedKey(expanded ? null : d.key)}>
                            <i className={`bi bi-chevron-${expanded ? "up" : "down"} me-1`}></i>
                            Menü ekleri {Object.keys(d.upgrades).length > 0 && `(${Object.keys(d.upgrades).length})`}
                          </button>
                          {expanded && (
                            <div className="dinein-upgrades">
                              {upgrades.map((u) => {
                                const qty = d.upgrades[u.id] ?? 0;
                                return (
                                  <div key={u.id} className="dinein-upgrade">
                                    <span>{u.menuUpgradeTitle} <em>{money(u.menuUpgradePrice, currencySymbol)}</em></span>
                                    <div className="dinein-stepper dinein-stepper--sm">
                                      <button type="button" disabled={qty <= 0} onClick={() => setUpgradeQty(d.key, u.id, qty - 1)}>
                                        <i className="bi bi-dash-lg"></i>
                                      </button>
                                      <span>{qty}</span>
                                      <button type="button" disabled={qty >= u.menuUpgradeMaxQuantity}
                                        onClick={() => setUpgradeQty(d.key, u.id, qty + 1)}>
                                        <i className="bi bi-plus-lg"></i>
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                              <div className="dinein-hint">Ek adedi yemek adediyle çarpılmaz — mutlak adettir.</div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {error && (
              <div className="dinein-alert dinein-alert--error"><i className="bi bi-exclamation-circle-fill"></i><span>{error}</span></div>
            )}

            <div className="dinein-picker__footer">
              <div className="dinein-picker__total">
                <span>Tahmini tutar</span>
                <strong>{money(draftTotal, currencySymbol)}</strong>
              </div>
              <button className="dinein-btn dinein-btn--primary dinein-btn--block"
                disabled={saving || draft.length === 0 || overLimit}
                onClick={handleSubmit}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-1" />Ekleniyor...</>
                  : <><i className="bi bi-plus-lg me-1"></i>Hesaba Ekle</>}
              </button>
              <div className="dinein-hint">Fiyat sunucuda hesaplanır; burada gördüğün tutar tahminidir.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
